import { Box, CircularProgress, Dialog } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  CommentArea,
  DialogActionsRow,
  DialogTitle,
  RatingButton,
  RatingLabel,
  RatingRow
} from "./ChatAgentFeedbackDialog.styles";
import { ButtonDigited } from "../../../../components/Button/Button.variants";


export type FeedbackVote = "up" | "down" | "neutral";

const ratingToVote = (rating: number): FeedbackVote => {
  if (rating >= 4) return "up";
  if (rating <= 2) return "down";
  return "neutral";
};

export interface FeedbackPayload {
  feedback: FeedbackVote;
  rating: number;
  comment?: string;
}

interface ChatAgentFeedbackDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: FeedbackPayload) => Promise<void>;
}

const RATING_VALUES = [1, 2, 3, 4, 5];

const ChatAgentFeedbackDialog = ({
  open,
  onClose,
  onSubmit
}: ChatAgentFeedbackDialogProps) => {
  const { t } = useTranslation();
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setRating(null);
      setComment("");
      setSubmitting(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (rating === null) return;
    setSubmitting(true);
    try {
      await onSubmit({
        feedback: ratingToVote(rating),
        rating,
        comment: comment.trim() || undefined
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={ open }
      onClose={ submitting ? undefined : onClose }
      maxWidth="sm"
      fullWidth
      sx={ {
        zIndex: 9999
      } }
      PaperProps={ {
        sx: {
          backgroundColor: "transparent",
          boxShadow: "none",
          overflow: "visible",
          zIndex: 9999
        }
      } }
    >
      <Box sx={ { padding: "16px" } }>
        <Card>
          <DialogTitle>
            { t("feedback_dialog_question") }
          </DialogTitle>

          <RatingRow>
            <RatingLabel>{ t("feedback_dialog_lowLabel") }</RatingLabel>
            { RATING_VALUES.map((value) => {
              const selected = rating === value;
              return (
                <RatingButton
                  key={ value }
                  selected={ selected }
                  disabled={ submitting }
                  onClick={ () => setRating(value) }
                  aria-label={ `rating ${value}` }
                >
                  { value }
                </RatingButton>
              );
            }) }
            <RatingLabel>{ t("feedback_dialog_highLabel") }</RatingLabel>
          </RatingRow>

          <CommentArea
            multiline
            minRows={ 4 }
            maxRows={ 6 }
            fullWidth
            inputProps={ { maxLength: 500 } }
            placeholder={ t("feedback_dialog_commentPlaceholder") || "" }
            value={ comment }
            onChange={ (e) => setComment(e.target.value) }
            disabled={ submitting }
          />

          <DialogActionsRow>
            <ButtonDigited 
              typology="secondary"
              onClick={ onClose } 
              disabled={ submitting }
              text={ t("feedback_dialog_cancel") }
            />
            <ButtonDigited 
              typology="primary"
              onClick={ handleSubmit }
              disabled={ rating === null || submitting }
              text={ t("feedback_dialog_submit") }
              endIcon={
                submitting ? (
                  <CircularProgress size={ 14 } sx={ { color: "#000" } } />
                ) : undefined
              }
            />
          </DialogActionsRow>
        </Card>
      </Box>
    </Dialog>
  );
};

export default ChatAgentFeedbackDialog;
